
/* © 2017 - Copyright of Aetonix Systems Inc - All Rights Reserved. Patent pending.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Aetonix, January 01, 2017
 * For information or permission request, email info@aetonixsystems.com
 */
import React from "react";
import {
	StyleSheet,
} from "react-native";
import FontAwesomeIcon from "react-native-vector-icons/FontAwesome";
import { createIconSetFromIcoMoon, createIconSet } from "react-native-vector-icons";
import aetonixIconSet from "../Resources/fonts/aetonix-icons/selection";
import genomiconIconSet from "../Resources/fonts/genomicons/map";

import Breakpoints from "react-native-dimensions-breakpoints";
import Style from "@aetonix/styles";

import { useAtaTextSize } from "@aetonix/hooks";

const AetonixIcon = createIconSetFromIcoMoon(aetonixIconSet, "icomoon");
const GenomiconIcon = createIconSet(genomiconIconSet, "SSGeomiconsSquared");

const AETONIX_ICONS = aetonixIconSet.icons
	.reduce(function(result, icon){
		const name = icon.properties.name;
		result[name] = true;
		return result;
	}, {});

const sizes = Style.text;

const sizeNames = Object.keys(sizes).sort(function(name1, name2){
	const size1 = sizes[name1];
	const size2 = sizes[name2];
	return size1 - size2;
});

const styles = StyleSheet.create({
	shiftGenomicon: {
		position: "relative",
		top: 2
	},
	disabled: {
		opacity: Style.look.disabled
	}
});

function smaller(name) {
	const position = sizeNames.indexOf(name);
	if(position < 1) return name;
	return sizeNames[position - 1];
}

function bigger(name, multiplier, max){
	if(!multiplier) return name;
	const position = sizeNames.indexOf(name) + multiplier;
	if(position > sizeNames.length - 2 || (max && sizeNames.indexOf(max) < position))
		return bigger(name, --multiplier);
	return sizeNames[position];
}

/**
 * This component takes the following props:
	 <color name> - which it maps to a color
	 <text size name> - which it maps to a named Style text size
	 size -  a string name which explicitly maps to a named Style text size
	 				OR a number that specifies a size in pixels (px)
	 style - which can contain ... styles
	 name - truncated name of the font-awesome icon (fa-iconname passed as 'iconname')
 */

export default function StyledIcon(props){
	const ataTextSize = useAtaTextSize();
	let color = props.color || Style.colors.secondary;
	const disabled = props.disabled;
	const maxSize = props.maxSize;

	if (props.black)
		color = Style.colors.black;
	else if (props.contrast)
		color = Style.colors.white;
	else if (props.alert)
		color = Style.look.alert;
	else if (props.red)
		color = Style.colors.red;
	else if (props.orange)
		color = Style.look.disconnected;
	else if (props.light)
		color = Style.colors.tertiaryLight;
	else if (props.teal)
		color = Style.colors.darkTeal;
	else if (props.tertiary)
		color = Style.colors.tertiary;
	// this interprets a 'name' prop as key for obtaining a named
	// style from Style.js
	let sizingName = sizeNames.reduce(function(found, name){
		if(found) return found;
		if(props[name]) return name;
		return "";
	}, "") || "standard";

	if(Breakpoints.smallerThan("mobile"))
		sizingName = smaller(sizingName);

	if(Breakpoints.smallerThan("small"))
		sizingName = smaller(sizingName);

	if(ataTextSize)
		sizingName = bigger(sizingName, ataTextSize, maxSize);

	let sizing = Style.text[sizingName];

	// the 'size' prop can be a name OR a number.  If specified
	// it defines the icon size explicitly (i.e. overides autoscale)
	const size = props.size;
	if (size) {
		if (typeof size === "string") {
			sizing = Style.text[props.size];
		}
		if (typeof size === "number") {
			sizing = size;
		}
	}

	const otherStyles = [{
		backgroundColor: "transparent"
	}, props.style];

	if(disabled){
		otherStyles.push(styles.disabled);
	}

	let name = props.name;

	let Icon = FontAwesomeIcon;

	const isAetonix = AETONIX_ICONS[name];
	if(isAetonix){
		Icon = AetonixIcon;
	}

	const isGenimicon = genomiconIconSet[name];
	if(isGenimicon){
		Icon = GenomiconIcon;
		otherStyles.unshift(styles.shiftGenomicon);
	}

	if(name.indexOf("fa-") === 0)
		name = name.slice(3);

	return (
		<Icon selectable={false} style={otherStyles} name={name} color={color} size={sizing} />
	);
}